// +----------------------------------------------------------------------
// | 编辑资料 /profile/edit（子页面）
// | 2026-09-29 从「我的资料」页拆出来：那边只留「个人信息」入口行，
// | 表单挪到这里。顶栏 = 主顶栏 + 返回钮（Layout 统一处理，同决定子页）。
// | 表单样式（.profile-gender/.profile-form-foot 等）与「我的资料」共用 Profile.less
// +----------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProfile, updateProfile } from '../../api/user';
import { toast } from '../../components/Toast';
import '../Profile/Profile.less';
import './ProfileEdit.less';

const GENDER_OPTIONS = [
  { value: 0, label: '不说' },
  { value: 1, label: '男' },
  { value: 2, label: '女' },
];

export default function ProfileEdit() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nickname: '',
    gender: 0,
    birthday: '',
    occupation: '',
    intro: '',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getProfile();
        if (!mounted) return;
        const user = data.user || {};
        const profile = data.profile || {};
        setForm({
          nickname: user.nickname || '',
          gender: profile.gender === null || profile.gender === undefined ? 0 : Number(profile.gender),
          birthday: (profile.birthday || '').slice(0, 10),
          occupation: profile.occupation || '',
          intro: profile.intro || '',
        });
      } catch (e) {
        if (mounted) toast(e.message || '资料没拉到', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // 存完回「我的」页：那边重新挂载会拉最新资料，头像/昵称自动跟上
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/profile');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!form.nickname.trim()) {
      toast('昵称不能为空', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nickname: form.nickname.trim(),
        gender: Number(form.gender),
        occupation: form.occupation.trim(),
        intro: form.intro.trim(),
      };
      if (form.birthday) payload.birthday = form.birthday;

      await updateProfile(payload);
      toast('已保存', 'success');
      goBack();
    } catch (err) {
      toast(err.message || '没保存上，再试一次', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page center muted">正在加载…</div>;
  }

  return (
    <div className="page profile-edit">
      <h1 className="profile-edit-title">编辑资料</h1>

      <form className="card profile-form" onSubmit={handleSave}>
        <div className="field">
          <label htmlFor="nickname">昵称</label>
          <input
            id="nickname"
            className="input"
            type="text"
            maxLength={32}
            value={form.nickname}
            onChange={(e) => setField('nickname', e.target.value)}
            placeholder="想让我怎么称呼你"
          />
        </div>

        <div className="field">
          <label>性别（可不说）</label>
          <div className="profile-gender">
            {GENDER_OPTIONS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`profile-gender-item ${Number(form.gender) === item.value ? 'is-active' : ''}`}
                onClick={() => setField('gender', item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="birthday">生日（可不说）</label>
          <input
            id="birthday"
            className="input"
            type="date"
            value={form.birthday}
            onChange={(e) => setField('birthday', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="occupation">职业（可不说）</label>
          <input
            id="occupation"
            className="input"
            type="text"
            maxLength={50}
            value={form.occupation}
            onChange={(e) => setField('occupation', e.target.value)}
            placeholder="比如：程序员、老师、自由职业"
          />
        </div>

        <div className="field">
          <label htmlFor="intro">一句话介绍</label>
          <textarea
            id="intro"
            className="input"
            rows={3}
            maxLength={200}
            value={form.intro}
            onChange={(e) => setField('intro', e.target.value)}
            placeholder="随便写点，能让我更懂你（可不说）"
          />
        </div>

        {/* 保存区和表单字段同住一张卡：分隔线明确圈进卡片，避免看起来悬在外面 */}
        <div className="profile-form-foot">
          <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </button>

          <p className="profile-note muted">
            资料填不填都不影响使用，只是让我少问几句。
          </p>
        </div>
      </form>
    </div>
  );
}
